<template>
  <div class="container container-sm mt-5">
    <div class="row section justify-content-center">
      <Card class="mb-4">
        <template #title><h3 class="text24-blue">{{ t("CHOOSE_PASSWORD") }}</h3></template>
        <template #content>
          <p id="password-policy">
            {{ t("SAFE_PW") }}:<br/>
            - {{ t("MIN_15_CHARS") }}<br/>
            - {{ t("CHARS_NUMS") }}<br/>
            - {{ t("LC_UC") }}<br/>
            - {{ t("NO_PERSONAL_DATA") }}<br/>
            - {{ t("NO_VOCABULARY") }}<br/>
          </p>

          <form @submit.prevent="setPassword">
            <div class="mb-2">
              <div class="input-group">
                <InputText
                    class="form-control rounded flex-grow-1"
                    name="currentPassword"
                    id="currentPassword"
                    :type="passwordHidden0 ? 'text' : 'password'"
                    :placeholder="t('CURRENT_PASSWORD')"
                    v-model="form.currentPassword"
                    required
                />
                <span v-if="form.currentPassword !== ''" class="input-group-text" style="cursor: pointer" @click="toggleShowPassword0">
                  <i :class="passwordHidden0 ? 'pi pi-eye-slash' : 'pi pi-eye'"/>
                </span>
              </div>
            </div>

            <div class="mb-2">
              <div class="input-group">
                <InputText
                    class="form-control rounded flex-grow-1"
                    name="password1"
                    id="password1"
                    :type="passwordHidden1 ? 'text' : 'password'"
                    :placeholder="t('NEW_PASSWORD')"
                    v-model="form.passwordFirst"
                    required
                    :invalid="passwordState() === false"
                />
                <span v-if="form.passwordFirst !== ''" class="input-group-text" style="cursor: pointer" @click="toggleShowPassword1">
                  <i :class="passwordHidden1 ? 'pi pi-eye-slash' : 'pi pi-eye'"/>
                </span>
              </div>
              <small v-if="pwFeedback()" class="text-danger">{{ pwFeedback() }}</small>
            </div>

            <div class="mb-2">
              <div class="input-group">
                <InputText
                    class="form-control rounded flex-grow-1"
                    name="password2"
                    id="password2"
                    :type="passwordHidden2 ? 'text' : 'password'"
                    :placeholder="t('CONFIRM_NEW_PASSWORD')"
                    v-model="form.passwordSecond"
                    required
                    :invalid="passwordState() === false"
                />
                <span v-if="form.passwordSecond !== ''" class="input-group-text" style="cursor: pointer" @click="toggleShowPassword2">
                  <i :class="passwordHidden2 ? 'pi pi-eye-slash' : 'pi pi-eye'"/>
                </span>
              </div>
            </div>

            <div class="mb-2 mt-4">
              <Button type="submit" :label="t('SAVE')" class="blue w-100"/>
            </div>
          </form>
        </template>
      </Card>
    </div>
  </div>
</template>

<style scoped>
#password-policy {
  font-size: 14px;
  color: dimgray;
}
</style>

<script setup>
import {computed, ref} from "vue";
import {injectAxios} from "@/util/inject";
import {useI18n} from "vue-i18n";
import {useToast} from "@/util/toastUtils";
import {isStrongPassword} from "@/util/validationUtils";

const {t} = useI18n();
const toast = useToast();
const axios = injectAxios();
const startValidation = ref(false);
const passwordHidden0 = ref(false);
const passwordHidden1 = ref(false);
const passwordHidden2 = ref(false);
const form = ref({
  currentPassword: '',
  passwordFirst: '',
  passwordSecond: '',
});

const passwordState = () => {
  if (form.value.currentPassword !== '' && form.value.currentPassword === form.value.passwordFirst) {
    return false;
  }
  if (form.value.passwordFirst !== form.value.passwordSecond) {
    return false;
  }
  if ((form.value.passwordFirst && !isStrongPassword(form.value.passwordFirst)) && (form.value.passwordSecond && !isStrongPassword(form.value.passwordSecond))) {
    return false;
  }
  if ((form.value.passwordFirst && isStrongPassword(form.value.passwordFirst)) && (form.value.passwordSecond && isStrongPassword(form.value.passwordSecond))) {
    return true;
  }
  return null;
}

function pwFeedback() {
  if (form.value.currentPassword !== '' && form.value.currentPassword === form.value.passwordFirst) {
    return t("NEW_PASSWORD_SAME_AS_CURRENT");
  }
  if ((form.value.passwordFirst && !isStrongPassword(form.value.passwordFirst)) && (form.value.passwordSecond && !isStrongPassword(form.value.passwordSecond))) {
    return t("INVALID_PW_PASSWORD");
  }
  if (form.value.passwordFirst !== form.value.passwordSecond) {
    return t("PASSWORD_MISMATCH");
  }
  return null;
}

const isValidAndMatch = computed(() => {
  return form.value.currentPassword !== ''
      && form.value.currentPassword !== form.value.passwordFirst
      && isStrongPassword(form.value.passwordFirst)
      && form.value.passwordFirst === form.value.passwordSecond;
});

const setPassword = async () => {
  startValidation.value = true;
  if (!isValidAndMatch.value) {
    toast.makeErrorToast(t("CHECK_INPUT"));
    return;
  }
  await axios.put("/account/password", {
    currentPassword: form.value.currentPassword,
    newPassword: form.value.passwordFirst,
  });
  toast.makeSuccessToast(t("OPERATION_SUCCESSFUL"));
  form.value.currentPassword = "";
  form.value.passwordFirst = "";
  form.value.passwordSecond = "";
};

function toggleShowPassword0() {
  passwordHidden0.value = !passwordHidden0.value;
}

function toggleShowPassword1() {
  passwordHidden1.value = !passwordHidden1.value;
}

function toggleShowPassword2() {
  passwordHidden2.value = !passwordHidden2.value;
}
</script>
